import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import ChatBox from '../components/ChatBox';
import PaymentModal from '../components/PaymentModal';
import RefundModal from '../components/RefundModal';
import { 
  MapPin, Phone, Mail, User, Shield, MessageCircle, 
  CreditCard, Eye, Trash2, AlertTriangle, Lock, Info,
  CheckSquare, Star 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotification } from '../context/NotificationContext';

const CompanyDashboard = () => {
  const { notify } = useNotification();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Data State
  const [purchasedLeads, setPurchasedLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [activeChatLead, setActiveChatLead] = useState(null);
  const [paymentLead, setPaymentLead] = useState(null);
  const [viewDetailsLead, setViewDetailsLead] = useState(null);
  const [refundLead, setRefundLead] = useState(null);
  const [reviewModal, setReviewModal] = useState(null); 

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Purchased Leads (Private Data)
      const purchasedRes = await api.get('company/my-leads/');
      // SAFEGUARD: Ensure it's an array
      const purchasedData = Array.isArray(purchasedRes.data) ? purchasedRes.data : [];
      setPurchasedLeads(purchasedData);

      // 2. Fetch Selected Leads (Public Data from Marketplace)
      const storedIds = JSON.parse(localStorage.getItem('my_selected_leads') || '[]');
      const urlId = searchParams.get('selected');
      
      // Handle new selection from URL
      if (urlId && !storedIds.includes(urlId)) {
        storedIds.push(urlId);
        localStorage.setItem('my_selected_leads', JSON.stringify(storedIds));
        navigate('/company/dashboard', { replace: true });
      }

      if (storedIds.length > 0) {
        const marketRes = await api.get('marketplace/');
        // SAFEGUARD: Ensure results exist
        const marketResults = marketRes.data.results || [];
        
        // Filter: Keep leads that are in our cart AND NOT already purchased
        const cartItems = marketResults.filter(l => 
          storedIds.includes(String(l.id)) && 
          !purchasedData.find(pl => pl.id === l.id)
        );
        setSelectedLeads(cartItems);
      }
    } catch (error) {
      console.error("Dashboard Error", error);
      // Safety: Redirect if unauthorized
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      // CRITICAL: Stop loading
      setLoading(false);
    }
  };

  const removeFromSelection = (id) => {
    const newSelection = selectedLeads.filter(l => l.id !== id);
    setSelectedLeads(newSelection);
    
    const storedIds = JSON.parse(localStorage.getItem('my_selected_leads') || '[]');
    const updatedIds = storedIds.filter(sid => String(sid) !== String(id));
    localStorage.setItem('my_selected_leads', JSON.stringify(updatedIds));
    
    notify('info', 'Lead removed from selection.');
  };

  const handlePaymentSuccess = async () => {
    if (!paymentLead) return;
    try {
      await api.post(`payment/process/${paymentLead.id}/`);
      setPaymentLead(null);
      notify('success', 'Payment Successful! Lead moved to Purchased list.');
      
      // Update UI locally
      const justPurchased = selectedLeads.find(l => l.id === paymentLead.id);
      setSelectedLeads(selectedLeads.filter(l => l.id !== paymentLead.id));
      
      const res = await api.get('company/my-leads/');
      setPurchasedLeads(res.data || []);
      
      const storedIds = JSON.parse(localStorage.getItem('my_selected_leads') || '[]');
      const updatedIds = storedIds.filter(sid => String(sid) !== String(paymentLead.id));
      localStorage.setItem('my_selected_leads', JSON.stringify(updatedIds));

    } catch (error) {
      notify('error', 'Payment failed.');
    }
  };

  const handleRefundSubmit = async (leadId, reason) => {
    try {
      await api.post(`refund/lead/${leadId}/`, { reason });
      setRefundLead(null);
      notify('success', "Refund request submitted successfully.");
    } catch (error) {
      notify('error', "Failed to submit request.");
    }
  };

  const handleCompleteSubmit = async (rating, comment) => {
    if(!reviewModal) return;
    try {
        await api.post(`company/leads/${reviewModal.id}/complete/`, {
            rating: rating,
            comment: comment
        });
        
        setReviewModal(null);
        notify('success', "Job marked as complete! Review sent.");
        loadData();
    } catch (error) {
        console.error(error);
        notify('error', "Failed to mark complete.");
    }
  };

  const formatCategory = (slug) => {
    if (!slug) return '';
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // --- CRITICAL FIX: RENDER LOADING SCREEN ---
  // This prevents the "map of undefined" crash
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 font-[Poppins]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8 font-[Poppins]">
      <div className="max-w-7xl mx-auto space-y-12">
        
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-extrabold text-gray-900">Company Dashboard</h1>
          <button onClick={() => navigate('/marketplace')} className="text-primary font-bold hover:underline">
            + Browse More Leads
          </button>
        </div>

        {/* --- SECTION 1: SELECTED LEADS LIST --- */}
        <section>
          <div className="flex items-center mb-4">
            <CreditCard className="text-primary mr-2" />
            <h2 className="text-xl font-bold text-gray-800">Selected Leads <span className="text-sm font-normal text-gray-500">(Pending Payment)</span></h2>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {selectedLeads.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <p>No leads selected.</p>
                <button onClick={() => navigate('/marketplace')} className="mt-2 text-primary font-bold hover:underline">Go to Marketplace</button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase tracking-wider">Location</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase tracking-wider">Appointment</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase tracking-wider">Price</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-blue-800 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {selectedLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-blue-50/20 transition-colors">
                        <td className="px-6 py-4">
                          <span className="font-bold text-gray-800 block">{formatCategory(lead.category)}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${lead.urgency === 'high' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'} uppercase`}>
                            {lead.urgency}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {lead.city}, {lead.state}, USA
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(lead.appointment_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 font-bold text-primary">
                          ${lead.price_company}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end space-x-2">
                            <button 
                              onClick={() => setViewDetailsLead({ ...lead, isPrivate: false })}
                              className="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center transition-transform hover:scale-105"
                            >
                              <Eye size={14} className="mr-1"/> View
                            </button>
                            <button 
                              onClick={() => setActiveChatLead(lead.id)}
                              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center transition-transform hover:scale-105"
                            >
                              <MessageCircle size={14} className="mr-1"/> Ask Admin
                            </button>
                            <button 
                              onClick={() => setPaymentLead(lead)}
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg text-xs font-bold shadow-md transition-transform hover:scale-105 border border-green-600"
                            >
                              Claim for ${lead.price_company}
                            </button>
                            <button 
                              onClick={() => removeFromSelection(lead.id)}
                              className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                              title="Remove from list"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* --- SECTION 2: PURCHASED LEADS LIST --- */}
        <section>
          <div className="flex items-center mb-4">
            <Shield className="text-green-600 mr-2" />
            <h2 className="text-xl font-bold text-gray-800">Purchased Leads <span className="text-sm font-normal text-gray-500">(Data Unlocked)</span></h2>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {purchasedLeads.length === 0 ? (
              <div className="p-12 text-center text-gray-500">You haven't purchased any leads yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Business Name</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Location</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Appointment</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-green-800 uppercase tracking-wider">Price Paid</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-green-800 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {purchasedLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-green-50/20 transition-colors">
                        <td className="px-6 py-4">
                          <span className="font-bold text-gray-900 block">{lead.business_name}</span>
                          <span className="text-xs text-gray-500">{formatCategory(lead.category)}</span>
                          {/* Visual Indicator if Completed */}
                          {lead.status === 'COMPLETED' && (
                              <span className="inline-block bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded mt-1">COMPLETED</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {lead.address}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(lead.appointment_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 font-bold text-gray-400">
                          ${lead.price_company}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end space-x-2">
                            <button 
                              onClick={() => setViewDetailsLead({ ...lead, isPrivate: true })}
                              className="bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center transition-transform hover:scale-105"
                            >
                              <Eye size={14} className="mr-1"/> Details
                            </button>
                            
                            <button 
                              onClick={() => setActiveChatLead(lead.id)}
                              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center transition-transform hover:scale-105"
                            >
                              <MessageCircle size={14} className="mr-1"/> Support
                            </button>

                            {/* --- NEW: MARK COMPLETE BUTTON (Only if not already completed/refunded) --- */}
                            {lead.status !== 'COMPLETED' && lead.status !== 'REJECTED' && (
                                <button 
                                    onClick={() => setReviewModal(lead)}
                                    className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center transition-transform hover:scale-105"
                                    title="Mark job as complete & review setter"
                                >
                                    <CheckSquare size={14} className="mr-1"/> Complete
                                </button>
                            )}
                            
                            {lead.status !== 'COMPLETED' && (
                                <button 
                                onClick={() => setRefundLead(lead)}
                                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center transition-transform hover:scale-105"
                                >
                                <AlertTriangle size={14} className="mr-1"/> Refund
                                </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

      </div>

      {/* --- MODALS --- */}
      {activeChatLead && <ChatBox leadId={activeChatLead} onClose={() => setActiveChatLead(null)} />}
      
      {paymentLead && <PaymentModal lead={paymentLead} onClose={() => setPaymentLead(null)} onConfirm={handlePaymentSuccess} />}
      
      <AnimatePresence>
        {refundLead && (
          <RefundModal 
            lead={refundLead} 
            onClose={() => setRefundLead(null)} 
            onSubmit={handleRefundSubmit} 
          />
        )}
      </AnimatePresence>

      {/* --- NEW: REVIEW & COMPLETE MODAL --- */}
      <AnimatePresence>
        {reviewModal && (
           <ReviewModal 
             lead={reviewModal}
             onClose={() => setReviewModal(null)}
             onSubmit={handleCompleteSubmit}
           />
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {viewDetailsLead && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
              <div className={`p-6 text-white flex justify-between items-center ${viewDetailsLead.isPrivate ? 'bg-green-700' : 'bg-blue-600'}`}>
                <h3 className="text-xl font-bold flex items-center">
                  {viewDetailsLead.isPrivate ? <Lock size={20} className="mr-2"/> : <Info size={20} className="mr-2"/>}
                  {viewDetailsLead.isPrivate ? "Confidential Details Unlocked" : "Appointment Overview"}
                </h3>
                <button onClick={() => setViewDetailsLead(null)} className="hover:bg-white/20 rounded p-1"><span className="text-2xl">&times;</span></button>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-bold">Category</p>
                    <p className="font-medium">{formatCategory(viewDetailsLead.category)}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-bold">Size</p>
                    <p className="font-medium">{viewDetailsLead.facility_size}</p>
                  </div>
                </div>

                {viewDetailsLead.isPrivate ? (
                  <div className="space-y-4 border-t pt-4 border-gray-100">
                    <div>
                      <p className="text-xs text-green-600 uppercase font-bold mb-1">Business Name</p>
                      <p className="text-lg font-bold text-gray-900">{viewDetailsLead.business_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold mb-1">Full Address</p>
                      <p className="text-gray-700">{viewDetailsLead.address}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold">Decision Maker</p>
                        <p className="font-medium">{viewDetailsLead.decision_maker_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold">Contact</p>
                        <p className="text-sm">{viewDetailsLead.phone}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 border-t pt-4 border-gray-100 text-center py-8">
                    <Shield size={48} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500 font-medium">Sensitive data is encrypted.</p>
                    <p className="text-sm text-gray-400 mb-6">
                      Business name, contact details, and address are hidden until payment is verified.
                    </p>
                    <button 
                      onClick={() => { setViewDetailsLead(null); setPaymentLead(viewDetailsLead); }}
                      className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-bold shadow-lg transition-transform hover:scale-105"
                    >
                      Unlock for ${viewDetailsLead.price_company}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- SUB-COMPONENT: REVIEW MODAL ---
const ReviewModal = ({ lead, onClose, onSubmit }) => {
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState('');

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
            <motion.div 
                initial={{ scale: 0.9, opacity: 0 }} 
                animate={{ scale: 1, opacity: 1 }} 
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            >
                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600">
                        <CheckSquare size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Mark Job Complete</h3>
                    <p className="text-sm text-gray-500 mt-2">
                        Please rate the quality of this lead from the setter. This helps maintain quality.
                    </p>
                </div>

                <div className="flex justify-center space-x-2 mb-6">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button 
                            key={star}
                            onClick={() => setRating(star)}
                            className={`p-1 transition-transform hover:scale-110 focus:outline-none ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                        >
                            <Star size={32} fill={star <= rating ? "currentColor" : "none"} />
                        </button>
                    ))}
                </div>

                <textarea
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none mb-6"
                    rows="3"
                    placeholder="Optional feedback for the setter..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                ></textarea>

                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition">Cancel</button>
                    <button 
                        onClick={() => onSubmit(rating, comment)}
                        className="flex-1 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 shadow-lg transition transform active:scale-95"
                    >
                        Confirm Completion
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

export default CompanyDashboard;